import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * QA drawer a11y helper (P2-FE-008):
 * - Esc closes
 * - simple focus trap inside panel
 * - restore focus to opener on close
 */
export function useQaDrawer({ open, onClose } = {}) {
  const panelRef = ref(null);
  let previouslyFocused = null;
  let removeKeydown = null;

  const focusables = () => {
    const root = panelRef.value;
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      el =>
        !el.hasAttribute('disabled') &&
        el.getAttribute('aria-hidden') !== 'true'
    );
  };

  const trapFocus = event => {
    if (event.key !== 'Tab') return;
    const nodes = focusables();
    if (!nodes.length) {
      event.preventDefault();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onKeydown = event => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      if (typeof onClose === 'function') onClose();
      return;
    }
    trapFocus(event);
  };

  const attach = async () => {
    previouslyFocused = document.activeElement;
    await nextTick();
    const nodes = focusables();
    (nodes[0] || panelRef.value)?.focus?.();
    if (!removeKeydown) {
      document.addEventListener('keydown', onKeydown, true);
      removeKeydown = () =>
        document.removeEventListener('keydown', onKeydown, true);
    }
  };

  const detach = () => {
    if (removeKeydown) {
      removeKeydown();
      removeKeydown = null;
    }
    const restore = previouslyFocused;
    previouslyFocused = null;
    if (restore && typeof restore.focus === 'function') {
      restore.focus();
    }
  };

  if (open) {
    watch(
      open,
      value => {
        if (value) attach();
        else detach();
      },
      { immediate: true }
    );
  }

  onBeforeUnmount(() => detach());

  return {
    panelRef,
    attach,
    detach,
  };
}
